import React from "react";
import { GroupMember, ProductDetailPageResult, Templates, VariationMembership } from "../ProductDetailPageResult";
import CustomersAlsoViewedItemSection from "../CustomersAlsoViewedItemSection";
import ProductBreakdowns from "../ProductBreakdowns";
import ChildOrNull from "~/common/ChildOrNull";
import ProductVariations from "../ProductVariations";
import ProductDetailSampling from "../ProductDetailSampling";
import { defaultUserObject } from "~/common/Store/rootReducer";
import ProductDetailHighlightsAndMetaSideSection from "../ProductDetailHighlightsAndMetaSideSection";
import ProductDetailBrandSideSection from "../ProductDetailBrandSideSection";
import tracking from "~/Gtm/Tracking/tracking";
interface SideColumnTopSectionProps {
  activeTemplate: ProductDetailPageResult;
  minimalPageVersion: boolean | undefined;
  groupMembers: GroupMember[];
  mounted: boolean;
}
const getHasVariations = (variationMembership: VariationMembership): boolean => {
  if (!variationMembership) {
    return false;
  }
  return variationMembership.variationGroups && !(variationMembership.variationGroups.length === 1 && variationMembership.variationGroups[0]?.variationGroupItems.length === 1);
};
const getActiveGroupMember = (groupMembers: GroupMember[], itemNumber: string): GroupMember | undefined => {
  return groupMembers?.find(groupMember => {
    return groupMember.itemNumber === itemNumber;
  });
};
const getMinimalPageVersionDynamicText = (variationMembership: VariationMembership): string => {
  return variationMembership?.variationGroups.length > 0 ? "More Options Available" : "View Full Product Details";
};
export const SideColumnTopSection = ({
  activeTemplate,
  minimalPageVersion,
  groupMembers,
  mounted
}: Readonly<SideColumnTopSectionProps>) => {
  const {
    primaryBrand,
    defaultCategory,
    itemNumber,
    highlights,
    stockMessage,
    isQuickShip,
    customersAlsoViewedCarouselItems,
    variationMembership,
    breakdownOptions = [],
    qualityRatingMembership,
    template,
    isVirtualPart,
    sampleProgram
  } = activeTemplate;
  const hasVariations = getHasVariations(variationMembership);
  const isVirtualGroupMaster = activeTemplate.groupingAttributes.isGroupMaster && activeTemplate.groupingAttributes.isVirtual;
  const activeGroupMember = getActiveGroupMember(groupMembers, activeTemplate.itemNumber);
  const hasAmountSelection = activeGroupMember?.configurationOptions?.some(option => {
    return option.name === "Amount";
  });
  const hasQuantityVariations = hasVariations && variationMembership?.variationGroups?.some(group => group.optionName === "Quantity");
  const displayBreakdownOptions = breakdownOptions && breakdownOptions.length > 1 && !isVirtualGroupMaster && !hasAmountSelection && !hasQuantityVariations;
  let selectedVariationItemNumber = itemNumber;
  if (isVirtualGroupMaster) {
    selectedVariationItemNumber = activeTemplate.originalId;
  }
  const showSampleProduct = sampleProgram && sampleProgram.isActive && sampleProgram.sampleProduct.isInStock;
  const isOutletTemplate = template === Templates.OutletTemplate;
  const minimalPageVersionDynamicText = getMinimalPageVersionDynamicText(activeTemplate.variationMembership);
  const handleFullProductDetailsClick = () => {
    window.location.assign(window.location.pathname);
  };
  return <div className={`side-col aside new-exp ${minimalPageVersion ? "pt-0 xs:pt-5 md:pt-0" : ""}`} data-sentry-component="SideColumnTopSection" data-sentry-source-file="index.tsx">
      {/* Show CAV as side tile if exactly 1 item in the collection. */}
      {customersAlsoViewedCarouselItems?.length === 1 && customersAlsoViewedCarouselItems[0] && <CustomersAlsoViewedItemSection customersAlsoViewedItem={customersAlsoViewedCarouselItems[0]} originalLinkingItemNumber={itemNumber} />}
      {!minimalPageVersion && (!!displayBreakdownOptions || hasVariations) && <nav aria-label="product variations" className="pb-0 xs:pb-5">
          <div className="box padded flex flex-col gap-4">
            <ChildOrNull condition={!!displayBreakdownOptions}>
              <ProductBreakdowns itemNumber={itemNumber} breakdownOptions={breakdownOptions} />
            </ChildOrNull>
            <ChildOrNull condition={hasVariations}>
              <ProductVariations itemNumber={selectedVariationItemNumber} variationMembership={variationMembership} isVirtualGroupMaster={!!isVirtualGroupMaster} />
            </ChildOrNull>
          </div>
        </nav>}
      {sampleProgram && showSampleProduct && <ProductDetailSampling maxQuantityPerOrder={sampleProgram.maxQuantityPerOrder} maxQuantityPerCustomer={sampleProgram.maxQuantityPerCustomer} mounted={mounted} labelType={sampleProgram.labelType} product={{
      description: sampleProgram.sampleProduct.title,
      feedIdentifier: sampleProgram.sampleProduct.feedIdentifier,
      imagePath: sampleProgram.sampleProduct.productImages[0]?.thumbnail,
      itemNumber: sampleProgram.sampleItemNumber,
      itemNumberId: sampleProgram.sampleItemNumberId,
      link: sampleProgram.sampleProduct.link,
      isInStock: sampleProgram.sampleProduct.isInStock,
      price: sampleProgram.sampleProduct.price.price,
      minimumBuy: sampleProgram.sampleProduct.minimumBuy,
      mustBuy: sampleProgram.sampleProduct.mustBuy,
      startBuy: sampleProgram.sampleProduct.startBuy,
      isWebstaurantPlusEligible: sampleProgram.sampleProduct.isWebstaurantPlusEligible,
      plusPrice: sampleProgram.sampleProduct.price.minimumAdvertisedPriceOverride.priceLevel5
    }} user={defaultUserObject} />}
      {(stockMessage?.length > 0 || isQuickShip || highlights?.length > 0 || highlights?.length > 0 || qualityRatingMembership || isOutletTemplate) && <ProductDetailHighlightsAndMetaSideSection isOutlet={isOutletTemplate} mounted={mounted} product={activeTemplate} />}
      {!isVirtualPart && primaryBrand.showBrandTile && <ProductDetailBrandSideSection primaryBrand={primaryBrand} defaultCategory={defaultCategory} />}
      {minimalPageVersion && <div className="m-4 xs:m-0">
          <tracking.Button theme="wss" size="large" aria-label={minimalPageVersionDynamicText} full onClick={() => handleFullProductDetailsClick()} className="white border border-solid text-green-500 !border-green-500 hover:bg-gray-100 hover:text-green-500" data-track="cpc_pdp_view_full_product_details" data-track-ei={itemNumber} data-testid="view-full-pdp-button">
            {minimalPageVersionDynamicText}
          </tracking.Button>
        </div>}
    </div>;
};